import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { 
  Users, Calendar, FileText, Bell, Clock, Download, Plus,
  Activity, Wallet, Gift, CheckCircle, UserPlus, Phone, MessageCircle, CreditCard
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../utils/api";
import { formatNumber } from "../utils/helpers";
import { escapeHtml, openPrintWindow } from "../utils/sanitize";

const DashboardPage = () => {
  const { token, isAdmin } = useAuth();
  const [stats, setStats] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [todayStats, setTodayStats] = useState({ operations: 0, revenue: 0, expenses: 0, patients: 0 });
  const [weekStats, setWeekStats] = useState({ operations: 0, revenue: 0, expenses: 0, net: 0 });
  const [recentOperations, setRecentOperations] = useState([]);
  const [pendingLeads, setPendingLeads] = useState([]);
  const [installmentStats, setInstallmentStats] = useState({ pending: [], received: [], totalPending: 0, totalReceived: 0 });
  const [loading, setLoading] = useState(true);
  const [zakatInfo, setZakatInfo] = useState({ dailyAmount: 0, monthlyAmount: 0, paidToday: false, paidThisMonth: false });
  const navigate = useNavigate();

  // Load zakat payment status from localStorage
  useEffect(() => {
    const savedZakat = localStorage.getItem("zakat_payments");
    if (savedZakat) {
      const payments = JSON.parse(savedZakat);
      const today = new Date().toISOString().split('T')[0];
      const thisMonth = today.substring(0, 7);
      setZakatInfo(prev => ({
        ...prev,
        paidToday: payments[today] || false,
        paidThisMonth: Object.keys(payments).some(d => d.startsWith(thisMonth) && payments[d])
      }));
    }
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, notificationsRes, patientsRes, transactionsRes, bookingsRes] = await Promise.all([
          api.get("/stats", token),
          api.get("/notifications", token),
          api.get("/patients", token),
          api.get("/transactions", token),
          api.get("/public/bookings", token).catch(() => ({ data: [] }))
        ]);
        setStats(statsRes.data);
        setNotifications(notificationsRes.data.slice(0, 5));
        
        // Set pending leads (new bookings not contacted yet)
        const pending = (bookingsRes.data || []).filter(b => b.status === "pending" || b.status === "new");
        setPendingLeads(pending.slice(0, 10));
        
        const patients = patientsRes.data || [];
        const transactions = transactionsRes.data || [];
        const today = new Date().toISOString().split('T')[0];
        const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        
        // Today's stats
        const todayOperations = patients.filter(p => p.operation_date === today);
        const todayRevenue = transactions
          .filter(t => t.date === today && t.transaction_type === "income")
          .reduce((sum, t) => sum + (t.amount || 0), 0);
        const todayExpensesTotal = transactions
          .filter(t => t.date === today && t.transaction_type === "expense")
          .reduce((sum, t) => sum + (t.amount || 0), 0);
        setTodayStats({
          operations: todayOperations.length,
          revenue: todayRevenue,
          expenses: todayExpensesTotal,
          patients: patients.filter(p => p.created_at?.startsWith(today)).length
        });
        
        // Week stats
        const weekTransactions = transactions.filter(t => t.date >= weekAgo);
        const weekRevenue = weekTransactions
          .filter(t => t.transaction_type === "income")
          .reduce((sum, t) => sum + (t.amount || 0), 0);
        const weekExpenses = weekTransactions
          .filter(t => t.transaction_type === "expense")
          .reduce((sum, t) => sum + (t.amount || 0), 0);
        const weekOperations = patients.filter(p => p.operation_date >= weekAgo && p.operation_date <= today);
        setWeekStats({
          operations: weekOperations.length,
          revenue: weekRevenue,
          expenses: weekExpenses,
          net: weekRevenue - weekExpenses
        });
        
        // Calculate Zakat (2.5% of NET profit, not revenue)
        const todayExpenses = transactions
          .filter(t => t.date === today && t.transaction_type === "expense")
          .reduce((sum, t) => sum + (t.amount || 0), 0);
        const todayNet = todayRevenue - todayExpenses;
        const dailyZakat = todayNet > 0 ? todayNet * 0.025 : 0;
        
        const monthlyRevenue = transactions
          .filter(t => t.date?.startsWith(today.substring(0, 7)) && t.transaction_type === "income")
          .reduce((sum, t) => sum + (t.amount || 0), 0);
        const monthlyExpenses = transactions
          .filter(t => t.date?.startsWith(today.substring(0, 7)) && t.transaction_type === "expense")
          .reduce((sum, t) => sum + (t.amount || 0), 0);
        const monthlyNet = monthlyRevenue - monthlyExpenses;
        const monthlyZakat = monthlyNet > 0 ? monthlyNet * 0.025 : 0;
        
        setZakatInfo(prev => ({
          ...prev,
          dailyAmount: dailyZakat,
          monthlyAmount: monthlyZakat,
          dailyNet: todayNet,
          monthlyNet: monthlyNet
        }));
        
        // Recent operations (last 5)
        const recent = patients
          .filter(p => p.operation_date)
          .sort((a, b) => b.operation_date.localeCompare(a.operation_date))
          .slice(0, 5);
        setRecentOperations(recent);
        
        // Installment tracking
        const installmentPatients = patients.filter(p => p.payment_type === "installment" && p.installment_amount > 0);
        const pendingInstallments = installmentPatients.filter(p => p.installment_status !== "received");
        const receivedInstallments = installmentPatients.filter(p => p.installment_status === "received");
        // المبلغ اللي ينزل = سعر العملية - خصم الشركة
        const calcCompanyTransfer = (p) => (p.price || 0) - (p.installment_amount || 0);
        setInstallmentStats({
          pending: pendingInstallments,
          received: receivedInstallments,
          totalPending: pendingInstallments.reduce((s, p) => s + calcCompanyTransfer(p), 0),
          totalReceived: receivedInstallments.reduce((s, p) => s + calcCompanyTransfer(p), 0),
          totalAll: installmentPatients.reduce((s, p) => s + calcCompanyTransfer(p), 0)
        });
        
      } catch (error) {
        toast.error("خطأ في تحميل البيانات");
      } finally {
        setLoading(false);
      }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  // Pay Zakat function
  const payZakat = async (type) => {
    const amount = type === "daily" ? zakatInfo.dailyAmount : zakatInfo.monthlyAmount;
    if (amount <= 0) {
      toast.error("لا يوجد مبلغ زكاة للدفع");
      return;
    }
    
    try {
      const today = new Date().toISOString().split('T')[0];
      await api.post("/transactions", {
        transaction_type: "expense",
        category: "زكاة",
        amount: amount,
        description: type === "daily" ? `زكاة يوم ${today}` : `زكاة شهر ${today.substring(0, 7)}`,
        payment_method: "نقدي",
        date: today
      }, token);
      
      // Save to localStorage
      const savedZakat = JSON.parse(localStorage.getItem("zakat_payments") || "{}");
      savedZakat[today] = true;
      localStorage.setItem("zakat_payments", JSON.stringify(savedZakat));
      
      setZakatInfo(prev => ({
        ...prev,
        paidToday: type === "daily" ? true : prev.paidToday,
        paidThisMonth: true
      }));
      
      toast.success(`تم دفع الزكاة: ${formatNumber(amount)}`);
    } catch (error) {
      toast.error("خطأ في تسجيل الزكاة");
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-pulse-soft text-gray-500">جاري التحميل...</div></div>;
  }

  const statCards = [
    { label: "إجمالي المرضى", value: stats?.total_patients || 0, icon: Users, color: "bg-blue-100 text-blue-600", link: "/patients" },
    { label: "إجمالي الجلسات", value: stats?.total_sessions || 0, icon: FileText, color: "bg-emerald-100 text-emerald-600", link: "/sessions" },
    { label: "المواعيد القادمة", value: stats?.upcoming_appointments || 0, icon: Calendar, color: "bg-amber-100 text-amber-600", link: "/appointments" },
    { label: "مراجعات قريبة", value: stats?.upcoming_followups || 0, icon: Bell, color: "bg-red-100 text-red-600", link: "/notifications" },
  ];

  // Export report function
  const exportReport = (type) => {
    const today = new Date().toLocaleDateString('en-GB');
    let reportData = {};
    let title = "";
    
    if (type === "daily") {
      title = `تقرير يومي - ${today}`;
      reportData = {
        "التاريخ": today,
        "عدد العمليات": todayStats.operations,
        "الإيرادات": todayStats.revenue.toLocaleString(),
        "مرضى جدد": todayStats.patients
      };
    } else if (type === "weekly") {
      title = `تقرير أسبوعي - ${today}`;
      reportData = {
        "الفترة": "آخر 7 أيام",
        "عدد العمليات": weekStats.operations,
        "الإيرادات": weekStats.revenue.toLocaleString(),
        "المصروفات": weekStats.expenses.toLocaleString(),
        "صافي الربح": weekStats.net.toLocaleString()
      };
    } else if (type === "monthly") {
      title = `تقرير شهري - ${new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}`;
      reportData = {
        "الشهر": new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }),
        "إجمالي المرضى": stats?.total_patients || 0,
        "إجمالي الجلسات": stats?.total_sessions || 0,
        "المواعيد القادمة": stats?.upcoming_appointments || 0
      };
    }
    
    // Create printable HTML report
    const printContent = `
      <!DOCTYPE html>
      <html dir="rtl" lang="ar">
      <head>
        <meta charset="UTF-8">
        <title>${title}</title>
        <style>
          body { font-family: 'Segoe UI', Tahoma, sans-serif; max-width: 600px; margin: 0 auto; padding: 30px; direction: rtl; }
          .header { text-align: center; border-bottom: 3px solid #10B981; padding-bottom: 20px; margin-bottom: 30px; }
          .header h1 { color: #1F2937; font-size: 22px; margin: 0 0 5px 0; }
          .header h2 { color: #10B981; font-size: 18px; margin: 0; }
          .report-date { color: #6B7280; font-size: 12px; margin-top: 10px; }
          .data-table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          .data-table th, .data-table td { padding: 12px 15px; text-align: right; border-bottom: 1px solid #E5E7EB; }
          .data-table th { background: #F3F4F6; font-weight: bold; color: #374151; }
          .data-table td { color: #1F2937; }
          .data-table tr:hover { background: #F9FAFB; }
          .footer { text-align: center; margin-top: 40px; color: #9CA3AF; font-size: 11px; }
          .value { font-weight: bold; color: #10B981; font-size: 16px; }
          @media print { body { padding: 10px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>المركز الوطني التركي لزراعة الشعر</h1>
          <h2>${title}</h2>
          <div class="report-date">تاريخ التصدير: ${new Date().toLocaleString('en-GB')}</div>
        </div>
        <table class="data-table">
          <tbody>
            ${Object.entries(reportData).map(([key, val]) => `
              <tr>
                <th>${escapeHtml(key)}</th>
                <td class="value">${escapeHtml(val)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        <div class="footer">
          <p>تقرير آلي من نظام إدارة المركز</p>
        </div>
        <script>window.onload = function() { window.print(); }</script>
      </body>
      </html>
    `;
    
    openPrintWindow(printContent);
    toast.success(`تم فتح ${title}`);
  };

  const markLeadContacted = async (leadId, e) => {
    e.stopPropagation();
    try {
      await api.put(`/public/bookings/${leadId}`, { status: "contacted" }, token);
      toast.success("تم تحديث الحالة");
      fetchData();
    } catch (err) { toast.error("خطأ في التحديث"); }
  };

  const confirmInstallmentReceived = async (patient, e) => {
    e.stopPropagation();
    if (!window.confirm(`تأكيد استلام مبلغ ${((patient.price || 0) - (patient.installment_amount || 0)).toLocaleString()} من ${patient.installment_company || "الشركة"} للمريض ${patient.name}؟`)) return;
    try {
      await api.put(`/patients/${patient.id}`, {
        installment_status: "received",
        installment_received_date: new Date().toISOString().split('T')[0]
      }, token);
      toast.success("تم تأكيد استلام القسط");
      fetchData();
    } catch (err) { toast.error("خطأ في التحديث"); }
  };

  return (
    <div className="animate-fade-in" data-testid="dashboard">
      <div className="page-header">
        <h1 className="page-title">لوحة التحكم</h1>
        <p className="page-subtitle">مرحباً بك في نظام المركز الوطني التركي لزراعة الشعر</p>
      </div>

      {/* Today & Week Summary Cards */}
      {isAdmin() && (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Today Card */}
        <div className="card p-5 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-blue-800 flex items-center gap-2">
              <Clock className="w-5 h-5" />
              ملخص اليوم
            </h3>
            <button onClick={() => exportReport("daily")} className="text-blue-600 hover:text-blue-800" title="تحميل التقرير">
              <Download className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-3 gap-2 sm:gap-4 text-center">
            <div>
              <div className="text-lg sm:text-2xl font-bold text-blue-700">{todayStats.operations}</div>
              <div className="text-[10px] sm:text-xs text-blue-600">عمليات</div>
            </div>
            <div>
              <div className="text-lg sm:text-2xl font-bold text-emerald-600">{todayStats.revenue.toLocaleString()}</div>
              <div className="text-[10px] sm:text-xs text-blue-600">إيرادات</div>
            </div>
            <div>
              <div className="text-lg sm:text-2xl font-bold text-red-500">{(todayStats.expenses || 0).toLocaleString()}</div>
              <div className="text-[10px] sm:text-xs text-blue-600">مصروفات</div>
            </div>
            <div>
              <div className={`text-lg sm:text-2xl font-bold ${(todayStats.revenue - (todayStats.expenses || 0)) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {(todayStats.revenue - (todayStats.expenses || 0)).toLocaleString()}
              </div>
              <div className="text-xs text-blue-600">صافي اليوم</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-purple-600">{todayStats.patients}</div>
              <div className="text-xs text-blue-600">مرضى جدد</div>
            </div>
          </div>
        </div>

        {/* Month Card */}
        <div className="card p-5 bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-purple-800 flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              ملخص الشهر
            </h3>
            <button onClick={() => exportReport("monthly")} className="text-purple-600 hover:text-purple-800" title="تحميل التقرير">
              <Download className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="p-2 bg-white/50 rounded-lg">
              <div className="text-xl font-bold text-purple-700">{stats?.total_patients || 0}</div>
              <div className="text-xs text-purple-600 mt-1">مرضى</div>
            </div>
            <div className="p-2 bg-white/50 rounded-lg">
              <div className="text-xl font-bold text-purple-600">{stats?.total_sessions || 0}</div>
              <div className="text-xs text-purple-600 mt-1">جلسات</div>
            </div>
            <div className="p-2 bg-white/50 rounded-lg">
              <div className="text-xl font-bold text-purple-500">{stats?.upcoming_appointments || 0}</div>
              <div className="text-xs text-purple-600">مواعيد</div>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Zakat Card */}
      {isAdmin() && (
      <div className="card p-5 mb-6 bg-gradient-to-br from-amber-50 to-yellow-100 border-amber-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-amber-800 flex items-center gap-2">
            <Gift className="w-5 h-5" />
            الزكاة (2.5% من الصافي)
          </h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Daily Zakat */}
          <div className="p-4 bg-white/50 rounded-xl">
            <div className="flex items-center justify-between mb-2">
              <span className="text-amber-700 font-medium">زكاة اليوم</span>
              {zakatInfo.paidToday ? (
                <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" /> مدفوعة
                </span>
              ) : (
                <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs">غير مدفوعة</span>
              )}
            </div>
            <div className="text-sm text-gray-500 mb-1">صافي اليوم: {formatNumber(zakatInfo.dailyNet || 0)}</div>
            <div className="text-2xl font-bold text-amber-800 mb-2">{formatNumber(zakatInfo.dailyAmount)}</div>
            {!zakatInfo.paidToday && zakatInfo.dailyAmount > 0 && (
              <button 
                onClick={() => payZakat("daily")}
                className="w-full btn btn-sm bg-amber-500 text-white hover:bg-amber-600"
              >
                دفع زكاة اليوم
              </button>
            )}
          </div>
          
          {/* Monthly Zakat */}
          <div className="p-4 bg-white/50 rounded-xl">
            <div className="flex items-center justify-between mb-2">
              <span className="text-amber-700 font-medium">زكاة الشهر</span>
              {zakatInfo.paidThisMonth ? (
                <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" /> مدفوعة
                </span>
              ) : (
                <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs">غير مدفوعة</span>
              )}
            </div>
            <div className="text-sm text-gray-500 mb-1">صافي الشهر: {formatNumber(zakatInfo.monthlyNet || 0)}</div>
            <div className="text-2xl font-bold text-amber-800 mb-2">{formatNumber(zakatInfo.monthlyAmount)}</div>
            {!zakatInfo.paidThisMonth && zakatInfo.monthlyAmount > 0 && (
              <button 
                onClick={() => payZakat("monthly")}
                className="w-full btn btn-sm bg-amber-500 text-white hover:bg-amber-600"
              >
                دفع زكاة الشهر
              </button>
            )}
          </div>
        </div>
      </div>
      )}

      {/* Installments Tracking */}
      {isAdmin() && (installmentStats.pending.length > 0 || installmentStats.received.length > 0) && (
      <div className="card mb-6 border border-blue-200" data-testid="installments-section">
        <div className="p-4">
          <h3 className="font-bold text-gray-800 flex items-center gap-2 mb-4">
            <CreditCard className="w-5 h-5 text-blue-600" />
            متابعة الأقساط
          </h3>
          
          {/* Stats */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="p-3 bg-red-50 rounded-xl text-center">
              <p className="text-xl font-bold text-red-600">{formatNumber(installmentStats.totalPending)}</p>
              <p className="text-xs text-red-500">بانتظار التحويل ({installmentStats.pending.length})</p>
            </div>
            <div className="p-3 bg-emerald-50 rounded-xl text-center">
              <p className="text-xl font-bold text-emerald-600">{formatNumber(installmentStats.totalReceived)}</p>
              <p className="text-xs text-emerald-500">تم الاستلام ({installmentStats.received.length})</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-xl text-center">
              <p className="text-xl font-bold text-blue-600">{formatNumber(installmentStats.totalAll)}</p>
              <p className="text-xs text-blue-500">إجمالي الأقساط</p>
            </div>
          </div>

          {/* Pending list */}
          {installmentStats.pending.length > 0 && (
            <div>
              <p className="text-sm font-bold text-red-700 mb-2">بانتظار التحويل من الشركة:</p>
              <div className="space-y-2">
                {installmentStats.pending.map(p => (
                  <div key={p.id} className="flex items-center justify-between p-3 bg-red-50 rounded-xl border border-red-100 hover:shadow-sm transition-shadow cursor-pointer"
                    onClick={() => navigate(`/patients/${p.id}`)}>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center text-white text-xs font-bold">
                        {(p.name || "?").charAt(0)}
                      </div>
                      <div>
                        <p className="font-bold text-sm text-gray-800">{p.name} {p.last_name || ""}</p>
                        <p className="text-xs text-gray-500">
                          {p.installment_company && <span>{p.installment_company} • </span>}
                          ينزل: <strong className="text-emerald-600">{((p.price || 0) - (p.installment_amount || 0)).toLocaleString()}</strong>
                          <span className="text-gray-400"> (خصم: {(p.installment_amount || 0).toLocaleString()})</span>
                          {p.installment_expected_date && <span> • متوقع: {p.installment_expected_date}</span>}
                        </p>
                      </div>
                    </div>
                    <button 
                      onClick={(e) => confirmInstallmentReceived(p, e)}
                      className="px-3 py-1.5 bg-emerald-500 text-white rounded-lg text-xs font-bold hover:bg-emerald-600 transition-colors flex items-center gap-1"
                      data-testid={`confirm-installment-${p.id}`}
                    >
                      <CheckCircle className="w-3.5 h-3.5" /> تم الاستلام
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recently received */}
          {installmentStats.received.length > 0 && (
            <div className={installmentStats.pending.length > 0 ? "mt-4" : ""}>
              <p className="text-sm font-bold text-emerald-700 mb-2">تم استلام الأقساط:</p>
              <div className="space-y-1">
                {installmentStats.received.slice(0, 5).map(p => (
                  <div key={p.id} className="flex items-center justify-between p-2 bg-emerald-50 rounded-lg cursor-pointer opacity-80 hover:opacity-100"
                    onClick={() => navigate(`/patients/${p.id}`)}>
                    <span className="text-sm text-gray-700">{p.name} {p.last_name || ""} - {p.installment_company}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-emerald-600">{((p.price || 0) - (p.installment_amount || 0)).toLocaleString()}</span>
                      <CheckCircle className="w-4 h-4 text-emerald-500" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Quick Access Shortcuts */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <button
          onClick={() => navigate("/patients")}
          className="p-4 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-xl hover:shadow-lg transition-all flex flex-col items-center gap-2"
          data-testid="quick-patients"
        >
          <Users className="w-8 h-8" />
          <span className="font-medium">المرضى</span>
        </button>
        {isAdmin() && (
        <button
          onClick={() => navigate("/accounting")}
          className="p-4 bg-gradient-to-br from-emerald-500 to-emerald-600 text-white rounded-xl hover:shadow-lg transition-all flex flex-col items-center gap-2"
          data-testid="quick-accounting"
        >
          <Wallet className="w-8 h-8" />
          <span className="font-medium">المحاسبة</span>
        </button>
        )}
        <button
          onClick={() => navigate("/notifications")}
          className="p-4 bg-gradient-to-br from-amber-500 to-amber-600 text-white rounded-xl hover:shadow-lg transition-all flex flex-col items-center gap-2"
          data-testid="quick-notifications"
        >
          <Bell className="w-8 h-8" />
          <span className="font-medium">التنبيهات</span>
        </button>
        <button
          onClick={() => navigate("/sessions")}
          className="p-4 bg-gradient-to-br from-indigo-500 to-indigo-600 text-white rounded-xl hover:shadow-lg transition-all flex flex-col items-center gap-2"
          data-testid="quick-sessions"
        >
          <FileText className="w-8 h-8" />
          <span className="font-medium">الجلسات</span>
        </button>
        <button
          onClick={() => navigate("/patients?new=true")}
          className="p-4 bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-xl hover:shadow-lg transition-all flex flex-col items-center gap-2"
          data-testid="quick-add-patient"
        >
          <Plus className="w-8 h-8" />
          <span className="font-medium">مريض جديد</span>
        </button>
      </div>

      {/* Pending Leads Alert */}
      {pendingLeads.length > 0 && (
        <div className="card mb-6 border-2 border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50" data-testid="pending-leads-section">
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-amber-800 flex items-center gap-2">
                <UserPlus className="w-5 h-5" />
                عملاء محتملين - تواصل معاهم! ({pendingLeads.length})
              </h3>
              <button onClick={() => navigate("/website-manage")} className="text-sm text-amber-600 hover:underline">
                عرض الكل
              </button>
            </div>
            <div className="space-y-2">
              {pendingLeads.map(lead => (
                <div key={lead.id} className="flex items-center justify-between p-3 bg-white rounded-xl border border-amber-200 hover:shadow-sm transition-shadow">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-bold text-sm">
                      {(lead.name || "?").charAt(0)}
                    </div>
                    <div>
                      <p className="font-bold text-gray-800 text-sm">{lead.name}</p>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span dir="ltr" className="text-blue-600 font-medium">{lead.phone}</span>
                        {lead.service && <span>• {lead.service}</span>}
                        {lead.created_at && <span>• {lead.created_at.split("T")[0]}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <a href={`tel:${lead.phone}`} className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"><Phone className="w-4 h-4" /></a>
                    <a href={`https://wa.me/${(lead.phone || "").replace(/^0/, "964")}`} target="_blank" rel="noreferrer" className="p-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors"><MessageCircle className="w-4 h-4" /></a>
                    <button
                      onClick={(e) => markLeadContacted(lead.id, e)}
                      className="px-3 py-1.5 bg-emerald-500 text-white rounded-lg text-xs font-bold hover:bg-emerald-600 transition-colors flex items-center gap-1"
                      data-testid={`mark-contacted-${lead.id}`}
                    >
                      <CheckCircle className="w-3.5 h-3.5" /> تم التواصل
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="stats-grid">
        {statCards.map((stat, index) => (
          <div 
            key={index} 
            className="stat-card card-hover cursor-pointer" 
            data-testid={`stat-card-${index}`}
            onClick={() => navigate(stat.link)}
          >
            <div className={`stat-icon ${stat.color}`}>
              <stat.icon className="w-6 h-6" />
            </div>
            <div className="stat-value">{stat.value}</div>
            <div className="stat-label">{stat.label}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        {/* Recent Operations */}
        <div className="data-table">
          <div className="table-header">
            <h3 className="table-title flex items-center gap-2">
              <Activity className="w-5 h-5 text-emerald-500" />
              آخر العمليات
            </h3>
          </div>
          <div className="p-4">
            {recentOperations.length === 0 ? (
              <div className="text-center py-4 text-gray-500 text-sm">لا توجد عمليات</div>
            ) : (
              <div className="space-y-2">
                {recentOperations.map((p) => (
                  <div 
                    key={p.id} 
                    className="flex items-center justify-between p-2 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100"
                    onClick={() => navigate(`/patients/${p.id}`)}
                  >
                    <span className="font-medium text-sm">{p.name}</span>
                    <span className="text-gray-500 text-xs">{p.operation_date}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Notifications */}
        <div className="data-table">
          <div className="table-header">
            <h3 className="table-title flex items-center gap-2">
              <Bell className="w-5 h-5 text-amber-500" />
              التنبيهات
            </h3>
            <button
              onClick={() => navigate("/notifications")}
              className="text-sm text-blue-600 hover:underline"
            >
              عرض الكل
            </button>
          </div>
          <div className="p-4">
            {notifications.length === 0 ? (
              <div className="text-center py-4 text-gray-500 text-sm">لا توجد تنبيهات</div>
            ) : (
              <div className="space-y-2">
                {notifications.map((notif) => (
                  <div 
                    key={notif.id} 
                    className={`p-2 rounded-lg cursor-pointer ${notif.type === "lead" ? "bg-amber-100 hover:bg-amber-200" : "bg-amber-50 hover:bg-amber-100"}`}
                    onClick={() => notif.patient_id ? navigate(`/patients/${notif.patient_id}`) : navigate("/website-manage")}
                  >
                    <div className="font-medium text-sm">{notif.type === "lead" ? "🔔 " : ""}{notif.patient_name || notif.message}</div>
                    <div className="text-xs text-gray-500">{notif.message}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
